import React, { useCallback } from 'react';
import { evaluateExpression } from '@common/utils/evaluateExpression';

import Select from '../../shared/reactSelect/select.component';
import TruncatableSelect from '../../shared/reactSelect/truncatableSelect.component';
import { Option } from '../../shared/types/common';
import { StandardReportDefinition } from '../../shared/types/batchReports';
import { ParamValues } from '../../shared/types/customReports';
import { DefaultReportValues } from './types';
import UserRoles from '../../shared/authorization/userRoles';


type RenderParamProps = {
  name: string,
  reportParamValues: Record<string, string>,
  updateValue: (newValue: Record<string, string | string[]>) => void,
  isLoading: boolean,
  options: Option[]
  type: string,
  placeholder?: string
  defaultValues?: DefaultReportValues[string],
  disable: boolean
}

const renderSelect = ({
  name,
  reportParamValues,
  updateValue,
  isLoading,
  options = [],
  disable,
}: RenderParamProps) => {
  const onChange = ({ value }: Option) => {
    updateValue({
      [name]: value,
    });
  };

  return (<Select
    clearable={false}
    name={name}
    options={options}
    onChange={onChange} // eslint-disable-line react/jsx-no-bind
    scrollMenuIntoView={false}
    value={reportParamValues[name]}
    isLoading={isLoading}
    className="batch-reports-select"
    disabled={disable}
  />);
};

const renderMultiSelect = ({
  name,
  reportParamValues,
  updateValue,
  isLoading,
  options = [],
  placeholder,
  defaultValues = {},
  disable,
}: RenderParamProps) => {
  const onChange = (values: Option[]) => {
    updateValue({
      [name]: values.map(({ value }) => value),
    });
  };

  return (<TruncatableSelect
    clearable
    multi
    removeSelected
    virtualized
    withAll
    closeOnSelect={false}
    onChange={onChange} // eslint-disable-line react/jsx-no-bind
    name={name}
    options={options}
    scrollMenuIntoView={false}
    value={reportParamValues[name] || defaultValues[name]?.value}
    isLoading={isLoading}
    className="batch-reports-multiselect"
    placeholder={placeholder}
    disabled={disable}
  />);
};

const renderParam = (props: RenderParamProps) => {
  if (props.type === 'select') {
    return renderSelect(props);
  }
  if (props.type === 'multiselect') {
    return renderMultiSelect(props);
  }
  return null;
};

type ReportRowParamsProps = {
  reportDefinition: StandardReportDefinition,
  reportParamValues: ParamValues,
  updateReportParamValues: (itemName: string, newParameters: Record<string, string>) => void,
  options: Record<string, Option[]>,
  isLoading: boolean,
  defaultValues: DefaultReportValues[string],
  archivalFlag: boolean,
  restrictBatchDefaults: boolean,
  userRole: string,
  editable:boolean
}

export const ReportRowParams = ({
  reportParamValues,
  reportDefinition,
  updateReportParamValues,
  options,
  isLoading,
  defaultValues,
  archivalFlag,
  restrictBatchDefaults,
  userRole,
  editable,
}: ReportRowParamsProps) => {
  const updateValue = useCallback(newParamValues => {
    const mergedParamValues = {
      ...(reportParamValues ? reportParamValues : {}),
      ...newParamValues,
    };

    updateReportParamValues(reportDefinition.reportName, mergedParamValues);
  }, [reportDefinition, reportParamValues, updateReportParamValues]);

  const isDisable = (label: string): boolean => {
    const labelName = label;
    if (archivalFlag && labelName && labelName.replace(/ /g, '').toLowerCase().includes('companycode')) {
      return true;
    }
    if (restrictBatchDefaults && userRole === UserRoles.SUPER_USER && !editable) {
      return true;
    }
    if (restrictBatchDefaults && editable) {
      return false;
    }
    if (restrictBatchDefaults) {
      return true;
    }
    return false;
  };
  return (
    <div className="custom-report-params-container">
      {Object.keys(reportDefinition.params).map(name => {
        const param = reportDefinition.params[name];
        const disable = isDisable(param.label);
        if (param.hideWhen && evaluateExpression(param.hideWhen, reportParamValues)) {
          return null;
        }
        return (
          <div className="custom-param-selector" key={name}>
            <div className="custom-param-label">{param.label}</div>
            {renderParam({
              updateValue,
              options: options[name],
              defaultValues,
              isLoading,
              type: param.type,
              name,
              reportParamValues,
              disable,
            })}
          </div>
        );
      })}
    </div>
  );
};

